import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { Edit } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { useMasterPermissions } from "../useMasterPermissions";
import {
  useGetHolidaysQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
} from "./holidaysApi";

// Mirrors GUI/Master/frm_holidays.cs, now against the live
// /api/v1/masters/holidays endpoints (handoff §9.3). isSunday: 0 = holiday
// (specific date), 1 = Sunday (recurring, picked by month) - matches the
// legacy radio-button values, not a real boolean. Reading requires
// Holidays.View (route-gated); writes gated by Holidays.Create/.Edit + an
// all-branch caller (§13.3).
const HolidaysPage = () => {
  const { canCreate, canEdit } = useMasterPermissions("Holidays");
  const { data: records = [], isLoading } = useGetHolidaysQuery();
  const [createHoliday, { isLoading: isCreating }] = useCreateHolidayMutation();
  const [updateHoliday, { isLoading: isUpdating }] = useUpdateHolidayMutation();
  const [deleteHoliday] = useDeleteHolidayMutation();

  const [mode, setMode] = useState("new");
  const [selectedId, setSelectedId] = useState(null);
  const [type, setType] = useState("holiday"); // "holiday" | "sunday"
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");

  const selectedRecord = records.find((r) => r.id === selectedId) || null;

  useEffect(() => {
    if (mode === "new") {
      setType("holiday");
      setDate("");
      setReason("");
    } else if (selectedRecord) {
      const isSunday = Number(selectedRecord.isSunday) === 1;
      setType(isSunday ? "sunday" : "holiday");
      setDate(dayjs(selectedRecord.date).format(isSunday ? "YYYY-MM" : "YYYY-MM-DD"));
      setReason(selectedRecord.reason || "");
    }
  }, [mode, selectedRecord]);

  const isReadOnly = mode === "view";
  const canWrite = mode === "new" ? canCreate : canEdit;

  const select = (id) => {
    setSelectedId(id);
    setMode("view");
  };
  const startEdit = () => setMode("edit");
  const startNew = () => {
    setSelectedId(null);
    setMode("new");
  };

  const handleSave = async () => {
    if (!date) {
      toast.error("Date is required");
      return;
    }
    const isoDate =
      type === "sunday"
        ? dayjs(date, "YYYY-MM").startOf("month").format("YYYY-MM-DDT00:00:00")
        : dayjs(date, "YYYY-MM-DD").format("YYYY-MM-DDT00:00:00");
    const body = { date: isoDate, reason: reason.trim(), isSunday: type === "sunday" ? 1 : 0 };
    try {
      if (mode === "edit" && selectedId != null) {
        await updateHoliday({ id: selectedId, ...body }).unwrap();
      } else {
        await createHoliday(body).unwrap();
      }
      toast.success("Holiday saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save holiday");
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    const confirmed = await confirmDelete({ title: "Delete this entry?" });
    if (!confirmed) return;
    try {
      await deleteHoliday(selectedRecord.id).unwrap();
      toast.success("Holiday deleted");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not delete holiday");
    }
  };

  const formatDate = (record) =>
    Number(record.isSunday) === 1
      ? dayjs(record.date).format("MMM YYYY")
      : dayjs(record.date).format("DD-MMM-YYYY");

  const columns = [
    { title: "Date", dataIndex: "date", render: (_, record) => formatDate(record) },
    { title: "Reason", dataIndex: "reason" },
    {
      title: "Type",
      dataIndex: "isSunday",
      render: (value) => (
        <span className={`badge ${Number(value) === 1 ? "text-bg-warning" : "badge-linesuccess"}`}>
          {Number(value) === 1 ? "Sunday" : "Holiday"}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button type="button" className="btn btn-link p-1" onClick={() => select(record.id)}>
          <Edit size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Holidays</h4>
              <h6>Holiday calendar and recurring Sunday off-days</h6>
            </div>
          </div>
        </div>

        {!canCreate && !canEdit && (
          <div className="alert alert-info">
            You have read-only access. Managing holidays requires Holidays.Create/.Edit and an
            all-branch administrator.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row">
              <div className="col-md-2 mb-3">
                <label className="form-label">Id</label>
                <input
                  type="text"
                  className="form-control"
                  value={mode === "new" ? "Auto-assigned" : selectedId ?? ""}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-4 mb-3">
                <fieldset disabled={isReadOnly || !canWrite}>
                  <div className="d-flex gap-4 mt-4">
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="type-holiday"
                        name="holidayType"
                        checked={type === "holiday"}
                        onChange={() => setType("holiday")}
                      />
                      <label className="form-check-label" htmlFor="type-holiday">Holiday</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="type-sunday"
                        name="holidayType"
                        checked={type === "sunday"}
                        onChange={() => setType("sunday")}
                      />
                      <label className="form-check-label" htmlFor="type-sunday">Sundays</label>
                    </div>
                  </div>
                </fieldset>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">{type === "sunday" ? "Month" : "Date"}</label>
                <input
                  type={type === "sunday" ? "month" : "date"}
                  className="form-control"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label">Reason</label>
                <input
                  type="text"
                  className="form-control"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
            </div>
            {(canCreate || canEdit) && (
              <div className="d-flex gap-2">
                {mode !== "view" && canWrite && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isCreating || isUpdating}
                  >
                    Save
                  </button>
                )}
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={startNew}>
                  Clear
                </button>
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={records} rowKey="id" loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolidaysPage;
